import { skipToken } from "@reduxjs/toolkit/query";
import type {
  KnowledgeBase,
  KnowledgeBaseAttachment,
} from "@repo/app-types/entities/knowledgeBase";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { TeammateAgentBranch } from "@repo/app-types/entities/teammateAgent";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { useCallback, useState } from "react";
import {
  useAllKnowledgeBasesQuery,
  useGetKnowledgeBaseAttachmentsQuery,
} from "../../api/entities/knowledgeBase.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { useAppSelector } from "../../hooks/hook.js";
import { useAutosaveDraft } from "../../hooks/useAutosaveDraft.js";
import {
  useDraftSaveStatus,
  type DraftSaveStatus,
} from "../../hooks/useDraftSaveStatus.js";
import {
  usePlaybookEditor,
  type UsePlaybookEditorResult,
} from "../../hooks/usePlaybookEditor.js";
import {
  useTeammateAgentConfigEditor,
  type UseTeammateAgentConfigEditorResult,
} from "../../hooks/useTeammateAgentConfigEditor.js";
import { useWaitForAgentPrompt } from "../../hooks/useWaitForAgentPrompt.js";
import { selectCompany } from "../company/companySlice.js";
import type { PublishableChanges } from "./publishButton.js";

/**
 * Everything the agent-config surfaces (legacy Configuration tab and the static
 * agent layout) need to render the shared toolbar, simulation slideover, and
 * the editable config blocks. Centralizing this here keeps the legacy tab and
 * the static tabs behaviorally identical.
 */
export interface TeammateAgentConfigController {
  teammateAgentId: TeammateId;
  playbookId: PlaybookId | null;
  isStaticAgent: boolean;
  isConfigurationLocked: boolean;
  /** The agent's assigned number, used as outbound caller ID and for inbound. */
  phoneNumber: string | null;
  /**
   * Whether this agent can place outbound calls at all: the company has
   * outbound voice agents, and the agent's live config has outbound calling
   * enabled.
   */
  isOutboundCallingAvailable: boolean;

  agentConfig: UseTeammateAgentConfigEditorResult;
  playbook: UsePlaybookEditorResult;

  /** All knowledge bases available to attach. */
  allKnowledgeBases: readonly KnowledgeBase[] | undefined;
  /** Attachments for the currently-selected knowledge base. */
  knowledgeBaseAttachments: readonly KnowledgeBaseAttachment[] | undefined;

  // Aggregate save/publish state
  isSaving: boolean;
  hasUnpublishedChanges: boolean;
  saveStatus: DraftSaveStatus;
  isPublishing: boolean;
  isDiscarding: boolean;
  isActionInProgress: boolean;
  publishableChanges: PublishableChanges;
  onPublish: () => Promise<void>;
  onDiscardChanges: () => Promise<void>;

  // Simulation ("Try this agent")
  isSimulationSlideoverOpen: boolean;
  setIsSimulationSlideoverOpen: (open: boolean) => void;
  simulationKey: number;
  isPreparingAgent: boolean;
  shouldUseLiveToolCalls: boolean;
  setShouldUseLiveToolCalls: (value: boolean) => void;
  handleOpenSimulation: () => Promise<void>;
  handleResetSimulation: () => void;

  handleDataExtractionFieldsChange: (fieldIds: string[]) => void;

  /**
   * Authoritatively persist any pending agent-config draft edits right now.
   * Used before triggering an action that reads the server-side draft (an
   * Ask Alpharun message, a proposal apply), mirroring the "Try this agent"
   * flush.
   */
  flushAgentConfigSave: () => Promise<boolean>;
  /** The playbook counterpart of `flushAgentConfigSave`. */
  flushPlaybookSave: () => Promise<boolean>;
}

/**
 * Owns the full lifecycle of teammate agent config editing: the agent-config
 * and playbook editors, autosave, publish/discard, and the "Try this agent"
 * simulation. Call this once per agent-config surface (the legacy
 * Configuration tab, or the static agent layout that provides it to its tabs
 * via context).
 */
export function useTeammateAgentConfigController(
  teammateAgentId: TeammateId,
): TeammateAgentConfigController {
  const { data: teammateAgent } = useGetTeammateAgentQuery({
    id: teammateAgentId,
    branch: TeammateAgentBranch.Live,
  });
  const teammateAgentConfig = teammateAgent?.teammate_agent_config;

  const company = useAppSelector(selectCompany);
  const playbookId = teammateAgentConfig?.playbook_id ?? null;
  const isOutboundVoiceAgentsEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.OutboundVoiceAgents,
  );

  const { data: allKnowledgeBases } = useAllKnowledgeBasesQuery();

  const [isSimulationSlideoverOpen, setIsSimulationSlideoverOpen] =
    useState(false);
  const [simulationKey, setSimulationKey] = useState(0);
  const [isPreparingAgent, setIsPreparingAgent] = useState(false);
  const [shouldUseLiveToolCalls, setShouldUseLiveToolCalls] = useState(false);

  const { waitForPromptReady } = useWaitForAgentPrompt({ teammateAgentId });

  const agentConfig = useTeammateAgentConfigEditor(teammateAgentId);
  const playbook = usePlaybookEditor(playbookId);

  const isStaticAgent = agentConfig.isStatic;

  // A realtime voice stack is configured via its meta prompt in HQ, so
  // its presence locks admin-side config for legacy agents. Static agents are
  // authored in admin (their prompt is built from the sections/settings edited
  // here), so the lock doesn't apply to them.
  const isConfigurationLocked =
    !isStaticAgent && (teammateAgentConfig?.uses_realtime_voice_stack ?? false);

  const {
    isSaving: isAgentConfigSaving,
    hasUnsavedChanges: agentConfigHasUnsavedChanges,
    hasUnpublishedChanges: agentConfigHasUnpublishedChanges,
    isValid: agentConfigIsValid,
    unpublishedChanges: agentConfigUnpublishedChanges,
    lastSavedAt: agentConfigLastSavedAt,
    onDiscardChanges: onAgentConfigDiscardChanges,
    onPublishChanges: onAgentConfigPublish,
    onSaveDraft: onAgentConfigSaveDraft,
  } = agentConfig;

  const {
    data: playbookData,
    liveData: playbookLiveData,
    isSaving: isPlaybookSaving,
    hasUnsavedChanges: playbookHasUnsavedChanges,
    hasUnpublishedChanges: playbookHasUnpublishedChanges,
    unpublishedChanges: playbookUnpublishedChanges,
    lastSavedAt: playbookLastSavedAt,
    onChange: onPlaybookChange,
    onDiscardChanges: onPlaybookDiscardChanges,
    onPublishChanges: onPlaybookPublish,
    onSaveDraft: onPlaybookSaveDraft,
  } = playbook;

  const selectedKnowledgeBaseId = playbookData?.knowledgeBaseIds?.[0];
  const { data: knowledgeBaseAttachments } =
    useGetKnowledgeBaseAttachmentsQuery(selectedKnowledgeBaseId ?? skipToken);

  // Track which action is in progress (for button loading states)
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const isActionInProgress = isPublishing || isDiscarding;

  // Set up autosave for agent config
  // Only autosave when data is valid to avoid 400 errors from the server
  const { flushPendingSave: flushAgentConfigSave } = useAutosaveDraft({
    hasChanges: agentConfigHasUnsavedChanges,
    canAutosave:
      teammateAgentId !== null &&
      !isConfigurationLocked &&
      !isAgentConfigSaving &&
      !isActionInProgress &&
      agentConfigIsValid,
    onSave: onAgentConfigSaveDraft,
  });

  // Set up autosave for playbook (only for existing playbooks)
  const { flushPendingSave: flushPlaybookSave } = useAutosaveDraft({
    hasChanges: playbookHasUnsavedChanges,
    canAutosave:
      playbookId !== null &&
      !isConfigurationLocked &&
      !isPlaybookSaving &&
      !isActionInProgress,
    onSave: onPlaybookSaveDraft,
  });

  // Compute combined save status for display
  const isSaving = isAgentConfigSaving || isPlaybookSaving;
  const hasUnpublishedChanges =
    agentConfigHasUnpublishedChanges || playbookHasUnpublishedChanges;

  // Use the most recent lastSavedAt between agent config and playbook
  const lastSavedAt = (() => {
    if (!agentConfigLastSavedAt && !playbookLastSavedAt) return null;
    if (!agentConfigLastSavedAt) return playbookLastSavedAt;
    if (!playbookLastSavedAt) return agentConfigLastSavedAt;
    return new Date(agentConfigLastSavedAt) > new Date(playbookLastSavedAt)
      ? agentConfigLastSavedAt
      : playbookLastSavedAt;
  })();

  const saveStatus = useDraftSaveStatus({
    hasUnpublishedChanges,
    isSaving,
    lastSavedAt,
  });

  const onPublish = useCallback(async () => {
    if (isConfigurationLocked) {
      showToast({
        variant: ToastVariant.Error,
        title: "Configuration is locked",
        description: "This agent is currently locked and cannot be edited.",
      });
      return;
    }

    setIsPublishing(true);
    try {
      // Publish agent config if there are unpublished changes
      if (agentConfigHasUnpublishedChanges) {
        await onAgentConfigPublish();
      }

      // Publish playbook if there are unpublished changes
      if (playbookHasUnpublishedChanges) {
        await onPlaybookPublish();
      }

      showToast({
        variant: ToastVariant.Success,
        title: "Changes published",
        description: "Your changes have been published successfully.",
      });
    } catch (error) {
      console.error("Failed to publish changes:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Failed to publish changes",
        description: "Please try again.",
      });
    } finally {
      setIsPublishing(false);
    }
  }, [
    agentConfigHasUnpublishedChanges,
    onAgentConfigPublish,
    playbookHasUnpublishedChanges,
    onPlaybookPublish,
    isConfigurationLocked,
  ]);

  const onDiscardChanges = useCallback(async () => {
    if (isConfigurationLocked) {
      return;
    }

    setIsDiscarding(true);
    try {
      // Discard agent config changes
      await onAgentConfigDiscardChanges();

      // Revert playbook draft to live via hook
      await onPlaybookDiscardChanges();
    } finally {
      setIsDiscarding(false);
    }
  }, [
    onAgentConfigDiscardChanges,
    onPlaybookDiscardChanges,
    isConfigurationLocked,
  ]);

  // The conversation lives in the simulation component; remounting it starts over.
  const handleResetSimulation = useCallback(() => {
    setSimulationKey((prev) => prev + 1);
  }, []);

  const handleOpenSimulation = useCallback(async () => {
    setIsPreparingAgent(true);

    try {
      // Flush any pending edits to draft first so the simulation uses the latest.
      // Going through the autosave flush (rather than the raw savers) clears the
      // debounce timer and chains after any in-flight autosave via `inFlightRef`,
      // so we don't fork the draft revision with a concurrent write. This also
      // marks the prompt as stale and enqueues background regeneration.
      const agentConfigSaved = await flushAgentConfigSave();
      const playbookSaved = await flushPlaybookSave();
      if (!agentConfigSaved || !playbookSaved) {
        throw new Error("Failed to save draft before simulation");
      }

      // Wait for the prompt to be regenerated
      await waitForPromptReady();

      handleResetSimulation();
      setIsSimulationSlideoverOpen(true);
    } catch (error) {
      console.error("Failed to prepare agent:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Failed to prepare agent",
        description: "Please try again.",
      });
    } finally {
      setIsPreparingAgent(false);
    }
  }, [
    flushAgentConfigSave,
    flushPlaybookSave,
    waitForPromptReady,
    handleResetSimulation,
  ]);

  // Handler for data extraction field changes
  const handleDataExtractionFieldsChange = useCallback(
    (fieldIds: string[]) => {
      onPlaybookChange({ gradingDataExtractionFieldIds: fieldIds });
    },
    [onPlaybookChange],
  );

  // For the publish dropdown, show granular unpublished changes
  const publishableChanges: PublishableChanges = {
    identityNameChanged: agentConfigUnpublishedChanges.identityNameChanged,
    identityRoleChanged: agentConfigUnpublishedChanges.identityRoleChanged,
    identityAdditionalContextChanged:
      agentConfigUnpublishedChanges.identityAdditionalContextChanged,
    toolIdsChanged: agentConfigUnpublishedChanges.toolIdsChanged,
    instructionSectionsChanged:
      agentConfigUnpublishedChanges.instructionSectionsChanged,
    // Gated: a company de-flagged mid-edit can still have staged settings, and
    // listing a change they have no surface to inspect or fix is worse than
    // saying nothing.
    callingSettingsChanged:
      isOutboundVoiceAgentsEnabled &&
      agentConfigUnpublishedChanges.settingsChanged,
    dataExtractionChanged: playbookUnpublishedChanges.dataExtractionChanged,
    playbookChanged: playbookUnpublishedChanges.treeChanged,
    knowledgeBasesChanged: playbookUnpublishedChanges.knowledgeBasesChanged,
    outcomeChanged: playbookUnpublishedChanges.outcomeChanged,
    summaryTemplateChanged: playbookUnpublishedChanges.summaryTemplateChanged,
    playbookInitialTemplate: playbookLiveData?.tree,
    playbookCurrentTemplate: playbookData?.tree,
  };

  return {
    teammateAgentId,
    playbookId,
    isStaticAgent,
    isConfigurationLocked,
    phoneNumber: teammateAgentConfig?.phone_number ?? null,
    isOutboundCallingAvailable:
      isOutboundVoiceAgentsEnabled &&
      (teammateAgentConfig?.outbound_calling_enabled ?? false),
    agentConfig,
    playbook,
    allKnowledgeBases,
    knowledgeBaseAttachments,
    isSaving,
    hasUnpublishedChanges,
    saveStatus,
    isPublishing,
    isDiscarding,
    isActionInProgress,
    publishableChanges,
    onPublish,
    onDiscardChanges,
    isSimulationSlideoverOpen,
    setIsSimulationSlideoverOpen,
    simulationKey,
    isPreparingAgent,
    shouldUseLiveToolCalls,
    setShouldUseLiveToolCalls,
    handleOpenSimulation,
    handleResetSimulation,
    handleDataExtractionFieldsChange,
    flushAgentConfigSave,
    flushPlaybookSave,
  };
}
